/**
 * Utilitaires de date/heure "heure d'Algerie" (UTC+1, fixe, l'Algerie
 * n'applique plus le changement d'heure depuis 1981).
 *
 * Contexte (bug signale par Nadjib le 08/09/2026, decouvert en testant
 * depuis la France) : partout ailleurs dans ce code, une date+heure de
 * rendez-vous ("2026-09-08" + "21:30") etait transformee en objet Date
 * JavaScript sans jamais preciser de fuseau (ex: `new Date("2026-09-08T21:30:00")`
 * ou date-fns `parse(...)`) - dans ce cas, le navigateur du visiteur suppose
 * SON PROPRE fuseau horaire local. Pour un visiteur dont le navigateur est
 * bien regle sur l'Algerie, ca ne pose jamais de probleme (fuseau navigateur
 * = fuseau attendu). Mais pour quiconque browse depuis un fuseau different
 * (diaspora, voyage, horloge mal reglee), la meme heure "21:30" est alors
 * comprise comme "21h30 dans SON fuseau a lui" au lieu de "21h30 en
 * Algerie" - un ecart silencieux qui fausse tous les calculs "ce RDV est-il
 * deja passe / trop tot / trop tard pour rejoindre la video".
 *
 * Ces fonctions corrigent ca a la source : elles ne dependent JAMAIS du
 * fuseau du navigateur, seulement de l'horodatage absolu reel (Date.now(),
 * identique partout dans le monde) et du decalage fixe UTC+1 de l'Algerie.
 *
 * Regle d'usage dans le reste du code :
 *  - Pour COMPARER (RDV passe/a venir/dans la fenetre de X minutes) :
 *    algeriaDateTimeToInstant(date, heure) -> objet Date utilisable avec
 *    isBefore/isAfter/getTime() comme n'importe quel autre Date.
 *  - Pour AFFICHER un libelle a l'utilisateur (ex. "Disponible a partir de
 *    21:27") : addMinutesToHHMM(heure, minutes) - ne jamais reformater un
 *    objet Date issu de algeriaDateTimeToInstant avec format(...,'HH:mm'),
 *    ca redonnerait l'heure locale du NAVIGATEUR, pas celle d'Algerie.
 */

const ALGERIA_UTC_OFFSET_MINUTES = 60;

/**
 * Convertit une date+heure de RDV ("YYYY-MM-DD", "HH:mm"), ENTENDUE COMME
 * HEURE D'ALGERIE, en un instant absolu reel (objet Date valide et
 * comparable avec isBefore/isAfter/getTime(), quel que soit le fuseau du
 * visiteur). Renvoie un Date invalide (comme le ferait `new Date("n'importe
 * quoi")`) si le format est inattendu, plutot que null, pour rester
 * utilisable sans verification supplementaire partout ou un `new Date(...)`
 * etait utilise avant.
 */
export function algeriaDateTimeToInstant(dateStr, timeStr) {
  const [y, m, d] = String(dateStr || '').split('-').map(Number);
  const [hh, mm] = String(timeStr || '').split(':').map(Number);
  if ([y, m, d, hh, mm].some((n) => Number.isNaN(n))) return new Date(NaN);
  return new Date(Date.UTC(y, m - 1, d, hh, mm) - ALGERIA_UTC_OFFSET_MINUTES * 60 * 1000);
}

/**
 * Heure actuelle REELLE en Algerie, au format "HH:mm" - independante du
 * fuseau du navigateur. A utiliser pour comparer a une liste de creneaux
 * ("08:00", "08:30", ...) qui sont eux aussi toujours en heure d'Algerie.
 */
export function algeriaNowHHMM() {
  const shifted = new Date(Date.now() + ALGERIA_UTC_OFFSET_MINUTES * 60 * 1000);
  const hh = String(shifted.getUTCHours()).padStart(2, '0');
  const mm = String(shifted.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/**
 * Date du jour REELLE en Algerie, au format "YYYY-MM-DD" - independante du
 * fuseau du navigateur (utile surtout autour de minuit, ou le jour
 * calendaire peut differer entre deux fuseaux).
 */
export function algeriaTodayYMD() {
  const shifted = new Date(Date.now() + ALGERIA_UTC_OFFSET_MINUTES * 60 * 1000);
  const y = shifted.getUTCFullYear();
  const m = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const d = String(shifted.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Additionne (ou soustrait, avec un nombre negatif) des minutes a une heure
 * "HH:mm", et renvoie le resultat au meme format "HH:mm" - pure
 * arithmetique sur les chiffres, sans jamais passer par un objet Date ni
 * son fuseau local. A utiliser pour tout LIBELLE affiche a l'utilisateur
 * (ex. "Disponible a partir de 21:27") - jamais pour une comparaison "est-ce
 * deja passe", qui doit utiliser algeriaDateTimeToInstant ci-dessus.
 */
export function addMinutesToHHMM(timeStr, minutesToAdd) {
  const [hh, mm] = String(timeStr || '').split(':').map(Number);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return timeStr;
  let total = hh * 60 + mm + minutesToAdd;
  total = ((total % (24 * 60)) + 24 * 60) % (24 * 60);
  const outHH = String(Math.floor(total / 60)).padStart(2, '0');
  const outMM = String(total % 60).padStart(2, '0');
  return `${outHH}:${outMM}`;
}
